import React, { useCallback, useContext } from 'react';
import DeleteBtn from './components/DeleteBtn';
import { IDeviceBankSettingForCreate, IRestaurantBankSettings } from 'types';
import RestaurantBankSettingsSelect from './components/RestaurantBankSettingsSelect';
import TerminalIdField from './components/TerminalIdField';
import M4LoginField from './components/M4LoginField';
import M4PasswordField from './components/M4PasswordField';
import {
	CreateDeviceContext,
	ICreateDeviceContext,
} from 'modules/Devices/CreateDevice/createDeviceState/createDeviceContext';
import { M4_SOFT_POS_QR_BANK_SETTINGS_ID, TOUCAN_BANK_SETTINGS_ID, TOUCAN_SBP_QR_BANK_SETTINGS_ID } from 'appConstants';
import ToucanLoginField from './components/ToucanLoginField';
import ToucanPasswordField from './components/ToucanPasswordField';
import style from './BankSettingsField.module.scss';

interface IBankSettingsField {
	data: IDeviceBankSettingForCreate;
}

const BankSettingsField: React.FC<IBankSettingsField> = ({ data }) => {
	const {
		changeRestaurantBankSettings,
		changeBankSettingsTerminalId,
		setFetchedRestaurantBankSettings,
		changeBankSettingsM4Login,
		changeBankSettingsM4Password,
		changeBankSettingsToucanLogin,
		changeBankSettingsToucanPassword,
		deleteBankSettingsBlock,
		IDSelectedRestaurant,
		fetchedRestaurantBankSettings,
	} = useContext<ICreateDeviceContext>(CreateDeviceContext);
	const acquirerId = fetchedRestaurantBankSettings.find(
		(restaurantBankSettings) => restaurantBankSettings.id === data.restaurantBankSettingId,
	)?.acquirer?.id;

	const handleChangeRestaurantBankSettings = useCallback(
		(value: string | null) => {
			data.id && changeRestaurantBankSettings(data.id, value);
		},
		[data.id, changeRestaurantBankSettings],
	);

	const handleChangeTerminalId = useCallback(
		(value: string) => {
			data.id && changeBankSettingsTerminalId(data.id, value);
		},
		[data.id, changeBankSettingsTerminalId],
	);

	const handleSetFetchedRestaurantBankSettings = useCallback(
		(value: IRestaurantBankSettings[]) => {
			setFetchedRestaurantBankSettings(value);
		},
		[setFetchedRestaurantBankSettings],
	);

	const handleChangeM4Login = useCallback(
		(value: string) => {
			data.id && changeBankSettingsM4Login(data.id, value);
		},
		[data.id, changeBankSettingsM4Login],
	);

	const handleChangeM4Password = useCallback(
		(value: string) => {
			data.id && changeBankSettingsM4Password(data.id, value);
		},
		[data.id, changeBankSettingsM4Password],
	);

	const handleChangeToucanLogin = useCallback(
		(value: string) => {
			data.id && changeBankSettingsToucanLogin(data.id, value);
		},
		[data.id, changeBankSettingsToucanLogin],
	);

	const handleChangeToucanPassword = useCallback(
		(value: string) => {
			data.id && changeBankSettingsToucanPassword(data.id, value);
		},
		[data.id, changeBankSettingsToucanPassword],
	);

	return (
		<div className={style.fieldsWrapper}>
			<div className={style.row}>
				<RestaurantBankSettingsSelect
					value={data.restaurantBankSettingId}
					settingsId={data.id || ''}
					IDSelectedRestaurant={IDSelectedRestaurant}
					onChange={handleChangeRestaurantBankSettings}
					setFetchedRestaurantBankSettings={handleSetFetchedRestaurantBankSettings}
				/>
				<TerminalIdField
					value={data.terminalId || ''}
					settingsId={data.id || ''}
					onChange={handleChangeTerminalId}
				/>
			</div>
			{acquirerId === M4_SOFT_POS_QR_BANK_SETTINGS_ID && (
				<div className={style.row}>
					<M4LoginField
						value={data.m4BankSettingCredentials?.login || ''}
						settingsId={data.id || ''}
						onChange={handleChangeM4Login}
					/>
					<M4PasswordField
						value={data.m4BankSettingCredentials?.password || ''}
						settingsId={data.id || ''}
						onChange={handleChangeM4Password}
					/>
				</div>
			)}
			{(acquirerId === TOUCAN_BANK_SETTINGS_ID || acquirerId === TOUCAN_SBP_QR_BANK_SETTINGS_ID) && (
				<div className={style.row}>
					<ToucanLoginField
						value={data.toucanBankSettingCredentials?.login || ''}
						settingsId={data.id || ''}
						onChange={handleChangeToucanLogin}
					/>
					<ToucanPasswordField
						value={data.toucanBankSettingCredentials?.password || ''}
						settingsId={data.id || ''}
						onChange={handleChangeToucanPassword}
					/>
				</div>
			)}
			<div className={style.buttons}>
				<DeleteBtn idForDeletion={data.id || ''} deleteBankSettingsBlock={deleteBankSettingsBlock} />
			</div>
			<div className={style.divider} />
		</div>
	);
};

export default BankSettingsField;
